import { useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import LoadingScreen from '../components/LoadingScreen'
import MyLearning from '../components/courses/MyLearning'
import { CATALOG_PATH, coursePath, slugify, studioCoursePath } from '../components/courses/courseUtils'
import { coursesAPI } from '../services/api'
import { useAuthStore } from '../store/authStore'
import type { Course, CourseLevel } from '../types'
import { apiErrorMessage } from '../utils/apiError'

const STATUS: Record<string, string> = { draft: 'Draft', published: 'Published' }
const ACCESS: Record<Course['access'], string> = { free: 'Free', subscribers: 'Subscribers', paid: 'Paid (not open yet)' }

/**
 * Courses (/courses): the courses you teach, and a new one. A course starts
 * as a draft only you can see; modules, lessons, access and publishing are
 * in its editor.
 */
const CourseStudio = () => {
  const user = useAuthStore((state) => state.user)
  const navigate = useNavigate()
  const [courses, setCourses] = useState<Course[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [level, setLevel] = useState<CourseLevel>('beginner')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!user) return
    coursesAPI
      .byInstructor(user.id)
      .then(setCourses)
      .catch((e) => setLoadError(apiErrorMessage(e, "Couldn't load your courses.")))
  }, [user])

  const create = async (e: FormEvent) => {
    e.preventDefault()
    const slug = slugify(title)
    if (!slug || !description.trim()) return
    setBusy(true)
    setError(null)
    try {
      const course = await coursesAPI.create({ title: title.trim(), slug, description: description.trim(), level, is_free: true })
      navigate(studioCoursePath(course.id))
    } catch (err) {
      setError(apiErrorMessage(err, "Couldn't create the course."))
      setBusy(false)
    }
  }

  if (!courses && !loadError) return <LoadingScreen />

  return (
    <div className="mx-auto max-w-4xl space-y-6" data-testid="course-studio">
      <header>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h1 className="text-2xl font-bold text-gray-900">Courses</h1>
          <Link to={CATALOG_PATH} className="text-sm text-primary-700 hover:underline">
            Browse all courses
          </Link>
        </div>
        <p className="mt-1 text-sm text-gray-600">
          Teach what you know. A course is free, or included with your subscription tiers. Its words pass the same
          check as your posts before it&rsquo;s published.
        </p>
      </header>

      <MyLearning />

      <h2 className="pt-2 text-lg font-semibold text-gray-900">Courses you teach</h2>
      {loadError && <p className="text-sm text-danger-700">{loadError}</p>}

      {courses && courses.length > 0 && (
        <ul className="divide-y divide-gray-200 rounded-lg bg-white shadow" data-testid="studio-courses">
          {courses.map((course) => (
            <li key={course.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
              <Link to={studioCoursePath(course.id)} className="font-medium text-gray-900 hover:text-primary-700">
                {course.title}
              </Link>
              <span
                className={`rounded px-2 py-0.5 text-xs font-medium ${
                  course.status === 'published' ? 'bg-success-50 text-success-700' : 'bg-gray-100 text-gray-700'
                }`}
              >
                {STATUS[course.status] || course.status}
              </span>
              <span className="text-xs text-gray-500">{ACCESS[course.access]}</span>
              <span className="text-xs text-gray-500">
                {course.enrollment_count} {course.enrollment_count === 1 ? 'student' : 'students'}
              </span>
              {course.status === 'published' && (
                <Link to={coursePath(course.slug)} className="ml-auto text-xs text-primary-700 hover:underline">
                  View page
                </Link>
              )}
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={create} className="space-y-3 rounded-lg bg-white p-5 shadow" aria-label="New course">
        <h2 className="font-semibold text-gray-900">New course</h2>
        <label className="block text-sm">
          <span className="text-gray-700">Title</span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
            placeholder="Position sizing from the stop"
          />
        </label>
        {title.trim() && (
          <p className="text-xs text-gray-500">
            Its page will be at {window.location.origin}
            {coursePath(slugify(title))}
          </p>
        )}
        <label className="block text-sm">
          <span className="text-gray-700">What it covers</span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
          />
        </label>
        <label className="block text-sm">
          <span className="text-gray-700">Level</span>
          <select
            value={level}
            onChange={(e) => setLevel(e.target.value as CourseLevel)}
            className="mt-1 rounded-md border border-gray-300 px-3 py-2"
          >
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
          </select>
        </label>
        <button
          type="submit"
          disabled={busy || !slugify(title) || !description.trim()}
          className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
        >
          {busy ? 'Creating…' : 'Create draft'}
        </button>
        {error && (
          <p role="alert" className="text-sm text-danger-700">
            {error}
          </p>
        )}
      </form>
    </div>
  )
}

export default CourseStudio
